/**
 * CutoffBanner — FOLLOWUP-004 (US-DELIVERY-PACKAGING-002).
 *
 * Surfaces on the Packaging list when the selected `date` has at
 * least one open `post_cutoff_order_change` review-queue item.
 * Clicking the banner deep-links to the operator inbox
 * (`/admin/review-queue`) with the kind filter preset so the
 * operator lands on the same records.
 *
 * #935 PR-C2 — repointed to the rebuilt review service via
 * `reviewQueueAPI.list` (`GET /api/review-queue`). The list endpoint
 * accepts a `kind` filter (server-side) so we narrow at fetch time and
 * returns the UNWRAPPED `ReviewQueueList = { items, total, ... }`
 * (open items by default). The per-row delivery_date is parsed from each
 * item's `details_json` blob, which preserves upstream-producer extras.
 * The inbox page is the `/admin/review-queue` route.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { reviewQueueAPI, REVIEW_QUEUE_KIND } from '@/services/review-queue';

function parseDeliveryDate(detailJson: string): string | undefined {
  try {
    const parsed = JSON.parse(detailJson) as Record<string, unknown>;
    return typeof parsed.delivery_date === 'string' ? parsed.delivery_date : undefined;
  } catch {
    return undefined;
  }
}

interface CutoffBannerProps {
  date: string;
}

export default function CutoffBanner({ date }: CutoffBannerProps) {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: queryKeys.exceptions.cutoff(date),
    queryFn: () =>
      reviewQueueAPI.list({
        filters: { kind: REVIEW_QUEUE_KIND.POST_CUTOFF_ORDER_CHANGE },
      }),
    staleTime: 30_000,
  });

  const matchCount = useMemo(() => {
    const rows = query.data?.items ?? [];
    return rows.filter((row) => parseDeliveryDate(row.details_json) === date).length;
  }, [query.data, date]);

  if (matchCount === 0) return null;

  // URL carries the kind filter the new inbox page consumes. `date`
  // is rendered in the banner copy but not in the link — the inbox
  // page has no date filter today, and embedding a no-op parameter
  // makes the URL contract lie about what happens on click.
  return (
    <Link
      to="/admin/review-queue?kind=post_cutoff_order_change"
      data-testid="packaging-cutoff-alert"
      className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive hover:bg-destructive/15 transition-colors"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{t('packaging.cutoff.title')}</p>
        <p className="text-xs opacity-90">
          {t('packaging.cutoff.body', { count: matchCount, date })}
        </p>
      </div>
    </Link>
  );
}
